import {useCallback, useEffect, RefObject} from 'react';

/**
 * The hook triggers the handler function when occur click inside the document but not inside the target element.
 * @param targetRef - the element click on which is prevent the handler call
 * @param handler - handler callback
 * @param active - pass false to temporary disable the hook
 */
export function useOutsideClick(targetRef: RefObject<HTMLElement>, handler?: () => void, active = true) {
	const handlerClick = useCallback(
		(event: MouseEvent) => {
			const topElement = document.body;

			let currentTarget = event.target as Element | null;
			while (currentTarget !== topElement && currentTarget) {
				if (currentTarget === targetRef.current) {
					return;
				}

				currentTarget = currentTarget.parentElement || null;
			}

			if (handler) {
				handler();
			}
		},
		[handler],
	);

	useEffect(() => {
		if (!active) {
			return;
		}

		document.addEventListener('click', handlerClick);

		return () => {
			document.removeEventListener('click', handlerClick);
		};
	}, [active, handlerClick]);
}

useOutsideClick.displayName = 'useOutsideClick';
